import { styledOmitCustomProps, Typography } from '@braskai/ui-kit';

import { css, styled } from '@mui/material';

export const QuizRoot = styled('main')(
  ({ theme }) => css`
    align-items: center;
    background-color: ${theme.palette.common.white};
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100dvh;
    justify-content: space-between;
    min-height: 720px;
    overflow-y: auto;
    padding: 96px 16px 56px;
    width: 100%;

    ${theme.breakpoints.up('desktop')} {
      padding: 180px 24px 60px;
    }
  `,
);

type QuizContentProps = {
  $desktopWidth: number;
};

export const QuizContent = styled(
  'div',
  styledOmitCustomProps,
)<QuizContentProps>(
  ({ $desktopWidth, theme }) => css`
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;

    ${theme.breakpoints.up('desktop')} {
      flex: 0 0 auto;
      width: ${$desktopWidth}px;
    }
  `,
);

export const Logo = styled('img')`
  display: block;
  height: 22px;
  width: 132px;
`;

export const QuestionArea = styled('div')(
  ({ theme }) => css`
    align-items: center;
    display: flex;
    flex: 1;
    width: 100%;

    ${theme.breakpoints.up('desktop')} {
      flex: 0 0 auto;
      margin-top: 40px;
    }
  `,
);

export const QuestionContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

export const QuestionHeader = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
`;

export const QuestionTitle = styled(Typography)(
  ({ theme }) => css`
    font-size: 24px;
    line-height: 30px;
    outline: none;

    ${theme.breakpoints.up('desktop')} {
      font-size: 30px;
      line-height: 36px;
    }
  `,
);

type OptionsGridProps = {
  $columns: number;
};

export const OptionsGrid = styled(
  'fieldset',
  styledOmitCustomProps,
)<OptionsGridProps>(
  ({ $columns, theme }) => css`
    border: 0;
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin: 0;
    min-inline-size: 0;
    padding: 0;

    ${theme.breakpoints.up('desktop')} {
      grid-template-columns: repeat(${$columns}, 204px);
    }
  `,
);

export const OptionLabel = styled('label')`
  display: block;
  min-height: 112px;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('desktop')} {
    min-height: 128px;
  }
`;

export const OptionInput = styled('input')(
  ({ theme }) => css`
    appearance: none;
    background: transparent;
    border: 0;
    border-radius: 16px;
    cursor: pointer;
    height: 100%;
    inset: 0;
    margin: 0;
    outline: none;
    position: absolute;
    width: 100%;
    z-index: 1;

    &:disabled {
      cursor: default;
    }

    &:focus-visible {
      box-shadow:
        0 0 0 2px ${theme.palette.common.white},
        0 0 0 4px #0000ff;
    }
  `,
);

type OptionCardContentProps = {
  $selected: boolean;
};

export const OptionCardContent = styled(
  'span',
  styledOmitCustomProps,
)<OptionCardContentProps>(
  ({ $selected, theme }) => css`
    align-items: flex-start;
    background-color: ${theme.palette.common.white};
    border: 1px solid ${$selected ? theme.palette.primary[500] : theme.palette.grey[200]};
    border-radius: 16px;
    box-shadow: ${$selected ? `0 0 0 2px ${theme.palette.common.white}, 0 0 0 4px #0000ff` : 'none'};
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: flex-start;
    min-height: 112px;
    overflow: hidden;
    padding: 16px;
    text-align: left;
    transition:
      border-color 300ms ease-out,
      box-shadow 300ms ease-out;
    width: 100%;

    ${OptionInput}:not(:disabled):hover + & {
      border-color: ${theme.palette.primary[500]};
    }

    ${theme.breakpoints.up('desktop')} {
      min-height: 128px;
    }
  `,
);

export const OptionText = styled('span')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

export const OptionIcon = styled('span')`
  display: flex;
  height: 20px;
`;

export const Actions = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 16px;
  min-height: 36px;
`;

export const ErrorContent = styled('div')`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ProgressRoot = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

type ProgressStepProps = {
  $active: boolean;
};

export const ProgressStep = styled(
  'span',
  styledOmitCustomProps,
)<ProgressStepProps>(
  ({ $active, theme }) => css`
    background-color: ${$active ? theme.palette.primary[500] : '#e7e7fc'};
    border-radius: 999px;
    height: 8px;
    transition: width 300ms ease-out;
    width: ${$active ? '24px' : '8px'};
  `,
);
